import { formatDate, isBefore, getHours, getMinutes, isSameDay, parseISO, set } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { tenantToday, toCalendarDay } from '@utils/timeZone';
import { isFirstVisible } from './header/helpers';
import { WorkloadData, WorkloadDraggableItem } from '@contexts/workload/types';
import { WorkloadCalendarSchedule } from '@services/workload/calendar/types';
import { getFirstDayOfBusinessWeek, getLastDayOfBusinessWeek } from '@contexts/workload/constants';
import { formatTime } from '@utils/i18n';
import { Errors } from '@hooks/errors/types';
import {
  CALENDAR_BORDERS,
  FOREMAN_COLUMN_WIDTH,
  MIN_DAY_COLUMN_WIDTH,
  MIN_VISIBLE_DAYS,
} from './constants';

const setWallClockTime = (date: Date, value: string, zone: string) => {
  if (!value) return date;
  const wallClock = toZonedTime(parseISO(value), zone);
  return set(date, { hours: getHours(wallClock), minutes: getMinutes(wallClock) });
};

export const getDroppedDateDetails = (
  startsAt: string,
  endsAt: string,
  draggableItem: WorkloadDraggableItem,
  zone: string,
) => [
  setWallClockTime(draggableItem.date, startsAt, zone),
  setWallClockTime(draggableItem.date, endsAt, zone),
];

export const toErrorMessages = (errors?: Errors): string[] => {
  if (!errors) return [];
  if (typeof errors === 'string') return [errors];
  if (Array.isArray(errors)) return errors;
  return Object.values(errors).flatMap((value) => (Array.isArray(value) ? value : [value]));
};

export const overlappingDetailsSummary = (newStartsAt: Date, newEndsAt: Date) =>
  `${[formatTime(new Date(newStartsAt || '')), formatTime(new Date(newEndsAt || ''))].join(' - ')}, ${formatDate(new Date(newStartsAt || ''), 'MMM do, yyyy')}`;

export const isFirstDayOfBusinessWeek = (date: Date): boolean =>
  date.getTime() === getFirstDayOfBusinessWeek(date).getTime();

export const isLastDayOfBusinessWeek = (date: Date): boolean =>
  date.getTime() === getLastDayOfBusinessWeek(date).getTime();

export const isBeforeToday = (date: Date): boolean => isBefore(toCalendarDay(date), tenantToday());

export const isLastVisible = (dates: Date[], index: number): boolean => index === dates.length - 1;

export const appendSchedulesToDates = (
  dates: Date[],
  schedules: WorkloadCalendarSchedule[],
): WorkloadCalendarSchedule[][] =>
  dates.map((date) =>
    schedules.filter((schedule) => isSameDay(date, parseISO(schedule.startDate))),
  );

export const getBoundaryItemStyles = (date: Date, index: number, upperHeader?: boolean) => [
  isFirstDayOfBusinessWeek(date) &&
    !isFirstVisible(index) &&
    'border-l-[5px] border-l-neutral-100 after:absolute after:left-[-5px] after:-top-1 after:h-1 after:w-[5px] after:bg-neutral-100',
  !upperHeader && isBeforeToday(date) && 'bg-neutral-50',
];

const TOLERANCE = 20;

const getThresholds = (initialDays: number, minDays: number) =>
  Array.from(
    { length: Math.max(initialDays - minDays, 0) },
    (_, index) =>
      FOREMAN_COLUMN_WIDTH +
      CALENDAR_BORDERS +
      (initialDays - index) * MIN_DAY_COLUMN_WIDTH +
      TOLERANCE,
  );

export const changeVisibleDays = (
  setData: (prop: keyof WorkloadData, value: unknown) => void,
  calendarWidth: number,
  visibleDays: number,
  initialDays: number,
  minDays = MIN_VISIBLE_DAYS,
) => {
  const THRESHOLDS = getThresholds(initialDays, minDays);
  let targetFewerDays = initialDays - visibleDays;

  while (targetFewerDays > 0 && calendarWidth >= THRESHOLDS[targetFewerDays - 1] + TOLERANCE) {
    targetFewerDays -= 1;
  }
  while (
    targetFewerDays < THRESHOLDS.length &&
    calendarWidth < THRESHOLDS[targetFewerDays] - TOLERANCE
  ) {
    targetFewerDays += 1;
  }

  if (targetFewerDays !== initialDays - visibleDays) {
    setData('visibleDays', initialDays - targetFewerDays);
  }
};

export const getDraggableStyles = (date: Date, draggableItem?: WorkloadDraggableItem) => {
  if (!draggableItem) return;
  if (!draggableItem?.isDragging) return;
  return isBeforeToday(date) ? 'bg-neutral-100' : 'bg-neutral-50';
};

export const getDayColumnWidth = (calendarWidth: number, visibleDays: number) =>
  Math.max(
    MIN_DAY_COLUMN_WIDTH,
    Math.floor((calendarWidth - FOREMAN_COLUMN_WIDTH - CALENDAR_BORDERS) / visibleDays),
  );
